/* eslint-disable react/display-name */
import { createSelector } from '@reduxjs/toolkit';
import {
  childrenSelector,
  Primitive,
  setChildrenAction,
  setLabelAction,
  setValueAction,
  valueSelector,
} from '@smartsheet-dataflow/formjs';
import { EntityId } from '@smartsheet-dataflow/formjs-react';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BridgeFormJSState } from '../../../services/forms/Types';
import { PartialBy } from '../../../utils/types';

interface Option {
  label: string;
  value: Primitive;
}

interface WithFormJSOptionsFromChildren {
  options: Option[];
}

const optionSelector = createSelector(
  (state: BridgeFormJSState) => state.label,
  (state: BridgeFormJSState) => state.value,
  (label, value) => (acc: Record<string | number, string>, id: string) => ({
    ...acc,
    [value[id] as string | number]: label[id],
  })
);

const optionsSelector = (id: string) =>
  createSelector(
    childrenSelector(id),
    optionSelector,
    (childIds = [], selector) => childIds.reduce(selector, {})
  );

export const withFormJSOptionsFromChildren =
  <P extends EntityId & WithFormJSOptionsFromChildren>(
    Component: React.ComponentType<P>
  ) =>
  (props: PartialBy<P, 'options'>) => {
    const values = (useSelector(valueSelector(props.id)) || []) as Array<
      string | number
    >;
    const valueMap = values.reduce(
      (
        acc: Record<string | number, string | number>,
        value: string | number
      ) => ({
        ...acc,
        [value]: value,
      }),
      {}
    );

    const optionMap = useSelector(
      optionsSelector(props.id),
      (
        a: Record<string | number, string>,
        b: Record<string | number, string>
      ) => JSON.stringify(a) === JSON.stringify(b)
    );

    const allOptionsMap = { ...valueMap, ...optionMap };

    const options = Object.entries(allOptionsMap).map(([value, label]) => ({
      label,
      value,
    }));

    return <Component {...(props as P)} options={options} />;
  };

interface WithFormJSCreatableOptions {
  onCreateOption?: (value: string) => void;
}

export const withFormJSCreatableOptions =
  <P extends EntityId & WithFormJSCreatableOptions>(
    Component: React.ComponentType<P>
  ) =>
  (props: PartialBy<P, 'onCreateOption'>) => {
    const dispatch = useDispatch();
    const setValue = setValueAction(dispatch);
    const setLabel = setLabelAction(dispatch);
    const setChildren = setChildrenAction(dispatch);
    const onCreateOption = useCallback(
      newValue => {
        const values = (useSelector(valueSelector(props.id)) ||
          []) as Primitive[];
        const children = useSelector(childrenSelector(props.id)) || [];
        if (newValue !== undefined) {
          setLabel({ inputId: `${props.id}.${newValue}`, label: newValue });
          setValue({ inputId: `${props.id}.${newValue}`, value: newValue });
          setValue({
            inputId: props.id,
            value: [...values, newValue],
          });
          setChildren({
            inputId: props.id,
            children: [...children, `${props.id}.${newValue}`],
          });
        }
      },
      [setLabel, props.id, setValue, setChildren]
    );

    return <Component onCreateOption={onCreateOption} {...(props as P)} />;
  };
